import React from 'react';
import { Flex } from '@radix-ui/themes';
import InstrumentationChip, { ChipType } from './index';

export interface InstrumentationCoverage {
  lists: number;
  alerts: number;
  workflows: number;
}

export interface InstrumentationChipGroupProps {
  /** Coverage data showing count of each instrumentation type */
  coverage: InstrumentationCoverage;
  /** Handler for clicking the lists chip */
  onListsClick?: () => void;
  /** Handler for clicking the alerts chip */
  onAlertsClick?: () => void;
  /** Handler for clicking the workflows chip */
  onWorkflowsClick?: () => void;
  /** Whether to show "add" chips for uninstrumented types */
  showAddChips?: boolean;
  /** Only show chips for types with non-zero counts */
  hideEmpty?: boolean;
  /** Disabled state for all chips */
  disabled?: boolean;
  /** Size of gap between chips */
  gap?: '1' | '2' | '3';
}

const InstrumentationChipGroup: React.FC<InstrumentationChipGroupProps> = ({
  coverage,
  onListsClick,
  onAlertsClick,
  onWorkflowsClick,
  showAddChips = false,
  hideEmpty = false,
  disabled = false,
  gap = '2',
}) => {
  const chips: Array<{
    type: ChipType;
    count: number;
    onClick?: () => void;
  }> = [
    { type: 'list', count: coverage.lists, onClick: onListsClick },
    { type: 'alert', count: coverage.alerts, onClick: onAlertsClick },
    { type: 'workflow', count: coverage.workflows, onClick: onWorkflowsClick },
  ];

  const visibleChips = hideEmpty
    ? chips.filter((chip) => chip.count > 0 || showAddChips)
    : chips;

  if (visibleChips.length === 0) {
    return null;
  }

  return (
    <Flex align="center" gap={gap} wrap="wrap">
      {visibleChips.map((chip) => {
        const isEmpty = chip.count === 0;
        const showAsAdd = showAddChips && isEmpty;

        return (
          <InstrumentationChip
            key={chip.type}
            type={chip.type}
            count={chip.count}
            variant={showAsAdd ? 'add' : 'count'}
            onClick={chip.onClick}
            disabled={disabled}
          />
        );
      })}
    </Flex>
  );
};

export default InstrumentationChipGroup;
